const DEFAULT_COMPACTION_PROMPT = `You are being asked to compact this conversation into a structured summary.
This summary will replace the full conversation history when the session
is restored. Produce a summary that lets you continue working effectively
without access to the original messages.

## Rules

PRESERVE (include verbatim):
- Current task/goal and its status
- All file paths that were read, created, or modified
- Function/class/variable names that are relevant to ongoing work
- Key decisions made and the reasoning behind them
- Errors encountered and how they were resolved
- Active flow node and state (if any)
- Unresolved questions or pending user input
- User names and who said/requested what (multi-user sessions)
- Test commands and verification criteria
- Any explicit user instructions about preferences or constraints

COMPRESS (summarize in one line each):
- Tool call / tool result pairs
- Intermediate reasoning steps
- Exploration paths that informed a decision

DROP (omit entirely):
- Full file contents (can be re-read from disk)
- Verbose command output (build logs, test suite output)
- Exploration that led nowhere and didn't inform decisions
- Redundant back-and-forth (corrections already applied)
- Transient UI state

## Format

Write a structured summary using these sections. Omit any section that
has no content.

### Goal
What the user is trying to accomplish.

### Progress
What has been done so far, organized by sub-task.

### Key Decisions
Decisions made and why, as a bulleted list.

### Active State
Current work in progress -- what was happening when compaction triggered.

### Files
All relevant file paths with a one-line note on each.

### Pending
What still needs to be done or is waiting on user input.

### Context
User preferences, constraints, or instructions that must carry forward.`;

/**
 * Merge instruction appended when a previous summary is carried forward. The
 * reply replaces that summary wholesale, so the model must restate it; without
 * this it answers with a delta that validation rejects as `too_short`.
 */
const RE_COMPACTION_MERGE_INSTRUCTION = `This conversation has already been compacted once. The summary below replaced
everything that came before it, and your reply will in turn replace this
summary -- anything you leave out is lost for good.

Your reply must be the COMPLETE merged summary in the section format above:
restate everything from the previous summary that still matters, then fold in
what has happened since. Do NOT reply with a delta, a diff, a list of changes,
or a note that the previous summary still applies.`;

/**
 * Assemble the final compaction prompt from layered sources.
 *
 * Resolution order:
 *   base = agentPrompt ?? DEFAULT_COMPACTION_PROMPT
 *   final = base + skillDirectives + previousSummary + focus
 *
 * `previousSummary` is the prior successful summary on a re-compaction; it is
 * reproduced verbatim so the model can merge it forward rather than answer
 * with a delta. Omit it on a first compaction.
 * @docLink packages/runner/dev-guide#compaction
 */
export function buildCompactionPrompt(opts: {
  agentPrompt?: string;
  skillDirectives?: string[];
  mcpDirectives?: string[];
  focus?: string;
  previousSummary?: string;
}): string {
  const base = opts.agentPrompt ?? DEFAULT_COMPACTION_PROMPT;
  const parts = [base];

  const allDirectives = [...(opts.skillDirectives ?? []), ...(opts.mcpDirectives ?? [])];

  if (allDirectives.length > 0) {
    parts.push(
      `\n\n## Additional Compaction Directives\n${allDirectives.map((d) => `- ${d}`).join("\n")}`,
    );
  }

  if (opts.previousSummary) {
    parts.push(
      `\n\n## Previous Summary\n\n<previous_summary>\n${opts.previousSummary}\n</previous_summary>\n\n${RE_COMPACTION_MERGE_INSTRUCTION}`,
    );
  }

  if (opts.focus) {
    parts.push(`\n\n## Focus\n${opts.focus}`);
  }

  return parts.join("");
}
